import { lazy, Suspense, useState } from "react";
import { createPortal } from "react-dom";
import { BlockEditor } from "./BlockEditor.js";
import { EDIT_SLOT_ID } from "../layout/RightSidebar.js";

// Milkdown is a real editor plus its widgets — several hundred KB that a
// reader never needs. Split it out so only opening the rich mode pays for it.
const MilkdownEditor = lazy(() =>
  import("./MilkdownEditor.js").then((m) => ({ default: m.MilkdownEditor })),
);

export type EditMode = "rich" | "markdown";

/**
 * The page editor: one switch, for the whole document.
 *
 * ✏️ **rich** is Milkdown — the everyday editor. `</>` **markdown** is
 * per-block raw editing. There is exactly one control, and it decides the mode
 * for the entire page rather than per block: a round of prototyping compared
 * this against a per-block `</>` gutter icon, and the toggle won — the gutter
 * button fought Crepe's own floating handle for the left margin, and per-block
 * granularity turned out finer than the problem needed.
 *
 * Both halves edit the same draft, so flipping is free: whatever you typed on
 * one side is what the other side opens. The unmount is deliberate — each side
 * re-reads the current draft on mount, which also re-runs Milkdown's
 * round-trip check, so flipping back shows you live what it would rewrite.
 *
 * Known rough edge: **undo does not cross the switch.** Flipping remounts the
 * editor, which resets ProseMirror's history. The draft is preserved; the undo
 * stack is not.
 */
export function PageEditor({
  draft,
  setDraft,
  path,
  repo,
  mode,
  setMode,
}: {
  draft: string;
  setDraft: (next: string) => void;
  path: string;
  repo: string;
  mode: EditMode;
  setMode: (m: EditMode) => void;
}) {
  const slot =
    typeof document !== "undefined" ? document.getElementById(EDIT_SLOT_ID) : null;

  const control = (
    <section className="dw-card dw-modeswitch-card">
      <header className="dw-card-head">Editing</header>
      <div className="dw-card-body">
        <span className="dw-modeswitch" role="group" aria-label="Editing mode">
          <button
            type="button"
            data-testid="dw-mode-rich"
            className={mode === "rich" ? "dw-mode-on" : ""}
            aria-pressed={mode === "rich"}
            title="Rich editing"
            onClick={() => setMode("rich")}
          >
            <span aria-hidden="true">✏️</span> rich
          </button>
          <button
            type="button"
            data-testid="dw-mode-markdown"
            className={mode === "markdown" ? "dw-mode-on" : ""}
            aria-pressed={mode === "markdown"}
            title="Raw markdown, block by block"
            onClick={() => setMode("markdown")}
          >
            <span aria-hidden="true">&lt;/&gt;</span> markdown
          </button>
        </span>
      </div>
    </section>
  );

  return (
    <>
      {slot && createPortal(control, slot)}
      {mode === "rich" ? (
        <Suspense fallback={<p className="dw-muted">loading the editor…</p>}>
          <MilkdownEditor draft={draft} setDraft={setDraft} path={path} repo={repo} />
        </Suspense>
      ) : (
        <BlockEditor draft={draft} setDraft={setDraft} path={path} repo={repo} />
      )}
    </>
  );
}
